import { Link } from "react-router-dom";
import { useEffect } from "react";
import MagazineLayout from "@/components/MagazineLayout";
import { Reveal } from "@/components/Reveal";
import BookCall from "@/components/BookCall";
import { ArrowRight, TrendingUp, Search, Repeat } from "lucide-react";

const MONTHS = [
  { m: "Moment 01", t: "Editorial Feature", d: "A 500–700 word Vogue-style profile that turns you into the story." },
  { m: "Moment 02", t: "S/ME Cover Shoot", d: "A cinematic, photorealistic cover made from your own photos." },
  { m: "Moment 03", t: "Magazine Advert", d: "A stylish full advert running for the month." },
  { m: "Moment 04", t: '"5 Questions"', d: "A rapid-fire editorial interview in your voice." },
  { m: "Moment 05", t: "The Noticeboard", d: "Your offer or event, postered to readers actively looking." },
  { m: "Moment 06", t: "The Members' Desk", d: "A bylined expert column that shows your authority." },
  { m: "Moment 07", t: "Social Pack", d: "Instagram, Facebook + TikTok posts, Stories, captions, hashtags — plus a ready-to-post 28-second article reel cut from your shots. Your handle tagged, collaborate invite sent." },
  { m: "Moment 08", t: "Seasonal Feature", d: "A second editorial angle to keep you fresh." },
  { m: "Moment 09", t: "Member Stories", d: "Short vertical spotlights and Stories for Instagram and Facebook — always tagged to your handle." },
  { m: "Moment 10", t: "Directory Listing", d: "Always live, always searchable — the whole year." },
  { m: "Moment 11", t: "SEO + GEO", d: "Optimised for Google AND AI answer engines. Always on." },
  { m: "Moment 12", t: "Cover Star Slot", d: "Earn the front cover — the ultimate S/ME moment." },
  { m: "Every edition", t: "The S/ME Newsletter", d: "Whenever you appear, your piece is mailed straight to reader inboxes — visibility beyond the page." },
];

export default function Membership() {
  useEffect(() => { document.title = "The Membership Year — S/ME"; }, []);
  return (
    <MagazineLayout>
      <div className="pt-[68px] bg-cream min-h-screen">
        {/* hero */}
        <section className="bg-ink text-cream px-5 sm:px-8 py-20 sm:py-28 grain">
          <div className="max-w-[1600px] mx-auto relative z-10">
            <Reveal>
              <p className="overline text-gloss-gold mb-5">The Membership Year</p>
              <h1 className="font-serif text-5xl sm:text-7xl lg:text-8xl tracking-tight leading-[0.9] max-w-4xl">Think in years, not months.</h1>
              <p className="font-sans text-lg sm:text-xl text-cream/75 mt-6 max-w-2xl leading-relaxed">
                S/ME isn't an ad you buy once. It's a year-long campaign that compounds. Every month you get something new — and every month your search visibility grows. The longer you're in the magazine, the more you get found.
              </p>
            </Reveal>
          </div>
        </section>

        {/* the machines only read */}
        <section className="px-5 sm:px-8 py-20 max-w-[1600px] mx-auto">
          <div className="max-w-3xl" data-testid="machines-read-block">
            <Reveal>
              <p className="overline text-gloss-gold mb-4">The part nobody else is selling you</p>
              <p className="font-serif text-2xl sm:text-3xl leading-snug text-ink">
                When someone asks Google — or ChatGPT — "best independent agency in Shoreditch" or "hat hire near me",
                those engines read and quote <em>written pages</em>. They can't see inside a TikTok.
              </p>
              <p className="font-sans text-base sm:text-lg text-ink/70 mt-6 leading-relaxed">
                Your S/ME feature is the page they find, read and recommend — a salesperson that never sleeps, never
                scrolls past, and gets stronger every month you're in the magazine. Video gets attention.{" "}
                <b className="text-ink">Written pages get chosen.</b>
              </p>
            </Reveal>
          </div>

          {/* the reader features that work for members */}
          <div className="mt-14 grid sm:grid-cols-3 gap-6 max-w-4xl" data-testid="reader-features-strip">
            {[
              ["Narrated features", "Every article carries a Listen button — a warm narrator reads your feature aloud, so even the busiest reader hears your story on the go."],
              ["The concierge sells you", "Readers ask \"who can do X near me?\" in our Ask S/ME desk — and it only ever recommends members. You're literally the answer."],
              ["Your article, as a reel", "Each feature becomes a free vertical film — a scrolling press-page reel or a photo montage with your contact details — ready to post, no editing needed. When we post it, we tag your handle and send a collaborate invite, so it lands on your grid too."],
            ].map(([t, d]) => (
              <div key={t} className="border-t-2 border-gloss-gold pt-4">
                <p className="font-serif text-xl text-ink">{t}</p>
                <p className="font-sans text-sm text-ink/65 mt-2 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* why annual */}
        <section className="px-5 sm:px-8 py-20 max-w-[1600px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Repeat, t: "12 drops a year", d: "A fresh piece of editorial content every single month — never stale, never forgotten." },
              { icon: Search, t: "Always-on SEO + GEO", d: "Optimised for Google and AI answer engines the entire year, not a one-off spike." },
              { icon: TrendingUp, t: "Visibility compounds", d: "More content + more time = more reach. The longer you stay, the better you rank." },
            ].map((b, i) => (
              <Reveal key={b.t} delay={i * 0.08} className="border-t-2 border-ink pt-6">
                <b.icon size={28} strokeWidth={1.5} className="mb-4" />
                <h3 className="font-serif text-2xl sm:text-3xl tracking-tight">{b.t}</h3>
                <p className="font-sans text-base text-ink/70 mt-3 leading-relaxed">{b.d}</p>
              </Reveal>
            ))}
          </div>
        </section>

        {/* the 12 */}
        <section className="bg-secondary px-5 sm:px-8 py-20">
          <div className="max-w-[1600px] mx-auto">
            <h2 className="font-grotesk text-3xl sm:text-4xl font-medium tracking-tight border-b border-ink/20 pb-5 mb-4">Everything you get in a year</h2>
            <p className="font-sans text-base text-ink/60 mb-12 max-w-2xl leading-relaxed">Twelve editorial moments, delivered one a month on a rotating schedule — in the order that suits your story best.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/15 border border-ink/15">
              {MONTHS.map((x, i) => (
                <Reveal key={x.m} delay={(i % 3) * 0.05} className={`p-7 ${x.m === "Every edition" ? "sm:col-span-2 lg:col-span-3 bg-ink text-cream" : "bg-cream"}`}>
                  <p className={`overline mb-2 ${x.m === "Every edition" ? "text-gloss-gold" : "text-gloss-silver"}`}>{x.m}</p>
                  <h3 className="font-serif text-2xl tracking-tight">{x.t}</h3>
                  <p className={`font-sans text-sm mt-2 leading-relaxed ${x.m === "Every edition" ? "text-cream/70" : "text-ink/70"}`}>{x.d}</p>
                </Reveal>
              ))}
            </div>
            <p className="font-mono text-xs text-ink/50 mt-6">Every item is produced inside S/ME for you — crafted in-house, editor-perfected. You just show up.</p>
          </div>
        </section>

        {/* cta */}
        <section className="bg-ink text-cream px-5 sm:px-8 py-24 grain">
          <div className="max-w-[1600px] mx-auto relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-8">
            <div>
              <h2 className="font-serif text-4xl sm:text-6xl tracking-tight leading-none">£300 a year. Two months free.</h2>
              <p className="font-sans text-lg text-cream/75 mt-4 max-w-xl">Commit to the year and let your visibility build while you run your business.</p>
            </div>
            <Link to="/join" className="inline-flex items-center gap-3 bg-cream text-ink px-8 py-5 overline hover:bg-gloss-silver transition-colors shrink-0" data-testid="membership-join">
              Join for the year <ArrowRight size={16} />
            </Link>
          </div>
          <div className="max-w-[1600px] mx-auto relative z-10 mt-8">
            <Link to="/join#honest-maths" className="overline text-gloss-gold link-underline" data-testid="membership-see-maths">
              See the honest maths — what this would cost bought separately →
            </Link>
          </div>
          <div className="max-w-[1600px] mx-auto relative z-10 mt-14">
            <Reveal>
              <BookCall />
            </Reveal>
          </div>
        </section>
      </div>
    </MagazineLayout>
  );
}
